import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Slider, ToggleButtonGroup, ToggleButton, IconButton, Collapse, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Line, Bar, ComposedChart } from 'recharts';
import { NumericFormat } from 'react-number-format';
import { apiService } from '../../services/apiService';

// 2026-03-22: RMD Tax Drag chart — accumulated tranches + beneficiary FA distributions
// Data from backend endpoint, no local computation
const TaxDragRmdChart = ({
  convAmt, setConvAmt, convTax,
  returnRate, setReturnRate, dragRate, setDragRate,
  currentAge, setCurrentAge, lifeYears, setLifeYears,
  distMethod, setDistMethod, yearsToStart, reinvestPct, distTaxRate
}) => {
  const [chartData, setChartData] = useState(null);
  const [showExplain, setShowExplain] = useState(false);

  // 2026-08-23: the balance whose RMDs the conversion avoids — gross conversion net of the
  // tax those displaced distributions would have owed, C(1-tD). Matches Appendix D.1.
  const afterTaxPrincipal = Math.round(convAmt * (1 - (distTaxRate || 0)));

  const fetchData = useCallback(async () => {
    try {
      const data = await apiService.getTaxDragChartData({
        conv_tax: convTax,
        conv_amt: convAmt,
        return_rate: returnRate / 100,
        drag_rate: dragRate / 100,
        method: distMethod,
        years_to_start: yearsToStart,
        life_years: lifeYears,
        current_age: currentAge,
        dist_reinvest_pct: reinvestPct,
        dist_tax_rate: distTaxRate || 0,
      });
      // Build cumulative benefit from yearly dist diffs
      const baseYear = new Date().getFullYear();
      // 2026-08-23: trad_bal already arrives scaled to the after-tax principal C(1-tD);
      // no display gross-up, so the line matches the paper's Appendix D.1 column.
      const year0 = {
        year: baseYear,
        age: currentAge,
        rothBal: 0, taxBal: 0, tradBal: afterTaxPrincipal,
        rothDist: 0, taxDist: 0,
        yearlyDiff: 0, cumulBenefit: 0,
      };
      let cumulBenefit = 0;
      const mapped = (data.years || []).map(row => {
          const distDiff = row.rmd_roth_dist - row.rmd_tax_dist;
          cumulBenefit += distDiff;
          return {
            year: row.year,
            age: row.age,
            rothBal: row.rmd_roth_bal,
            taxBal: row.rmd_tax_bal,
            tradBal: Math.round(row.trad_bal),
            rothDist: row.rmd_roth_dist,
            taxDist: row.rmd_tax_dist,
            yearlyDiff: distDiff,
            cumulBenefit: Math.round(cumulBenefit),
          };
        });
      setChartData({ years: [year0, ...mapped], rmd_fv: data.rmd_fv, rmd_pv: data.rmd_pv });
    } catch (err) {
      console.error('RMD tax drag chart error:', err);
    }
  }, [convAmt, convTax, returnRate, dragRate, yearsToStart, lifeYears, currentAge, distMethod, reinvestPct, distTaxRate, afterTaxPrincipal]);

  useEffect(() => {
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [fetchData]);

  if (!chartData || !chartData.years?.length) return null;

  return (
    <Box sx={{ mt: 0, mb: 0, border: '2px solid #b3e5fc', borderRadius: '8px', px: 0, pt: 0, pb: 1 }}>
      <Box sx={{ px: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: -0.5 }}>
        <Typography variant="subtitle1">
          <strong>RMD Reduction Alpha</strong> — Benefit of avoiding RMDs on ${afterTaxPrincipal?.toLocaleString()} of after-distribution-tax principal
        </Typography>
        <Tooltip title="Click for explanation" arrow>
          <IconButton size="small" onClick={() => setShowExplain(!showExplain)} sx={{ ml: 0.5, color: '#1976d2' }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={showExplain}>
        <Box sx={{ mb: 1, p: 1.5, backgroundColor: '#f0f7ff', borderLeft: '3px solid #2e7d32', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
            Converting ${convAmt?.toLocaleString()} of your Traditional account reduces your future taxable distributions and these yearly reductions can create an external conversion profit. The orange line is the <strong>after-tax principal</strong>, C(1&minus;tD) = ${afterTaxPrincipal?.toLocaleString()} — your conversion net of the tax those avoided distributions would have owed. That is the balance whose RMDs the conversion eliminates; it depletes via RMDs over your life, then a 10-year beneficiary annuity. The Conversion Amount box above shows the gross conversion.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 'bold', mb: 0.5 }}>
            REINVESTMENT IS KEY
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
            If you reinvest distributions in a taxable account at the tax-drag return of {(returnRate * (1 - dragRate / 100)).toFixed(2)}%, they earn less than if they'd been left in the Roth earning {returnRate.toFixed(1)}%. Reinvestment means you probably don't need the income as is the case with affluent investors who only take forced RMDs. Selecting 'RMD Only' as the distribution method assumes 100% of the distribution is reinvested to calculate the benefit of reduced RMDs.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 'bold', mb: 0.5 }}>
            RMD-ONLY DISTRIBUTIONS
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
            The ${convAmt?.toLocaleString()} conversion grows at {returnRate.toFixed(1)}% as it distributes. The RMDs are assumed to be 100% reinvested each year and grown at {returnRate.toFixed(1)}% and {(returnRate * (1 - dragRate / 100)).toFixed(2)}% during the investor's lifetime. The blue and red lines show these accumulating balances. After death, each balance is distributed to the beneficiaries in fixed payment amounts over 10 years. The difference between these distributions is that year's tax-drag avoidance profit, denoted by the green bars.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 'bold', mb: 0.5 }}>
            FIXED ANNUITY DISTRIBUTIONS
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
            We track the ${convAmt?.toLocaleString()} converted amount growing at {returnRate.toFixed(1)}% (blue line) and at {(returnRate * (1 - dragRate / 100)).toFixed(2)}% (red line) as both distribute according to the fixed-distribution schedule that depletes the account to zero over the investor's lifetime. The difference between these distributions is that year's tax-drag avoidance profit, denoted by the green bars.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
            People who take FA distributions probably need the income and will likely spend most or all of it and are therefore unlikely to reinvest distributions. This means the benefit calculation probably overstates the benefit for FA.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 'bold', mb: 0.5 }}>
            Reinvestment Percentage – New User Input?
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            100% distribution reinvestment is currently assumed in the benefit calculation. This makes sense for RMD-Only distributions, but probably not for Fixed Annuity distributions. Therefore, we'll soon add this user input.
          </Typography>
        </Box>
      </Collapse>

      {/* Inputs */}
      <Box display="flex" gap={2} flexWrap="nowrap" alignItems="center" sx={{ mb: 0 }}>
        <Box sx={{ minWidth: 120, maxWidth: 140 }}>
          <NumericFormat
            label="Conversion Amount"
            value={convAmt}
            onValueChange={(v) => setConvAmt(v.floatValue || 0)}
            thousandSeparator=","
            prefix="$"
            customInput={TextField}
            size="small"
            variant="outlined"
            fullWidth
            inputProps={{ style: { fontWeight: 'bold' } }}
          />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption">Return: {returnRate.toFixed(1)}%</Typography>
          <Slider
            value={returnRate}
            onChange={(e, v) => setReturnRate(v)}
            min={1} max={15} step={0.5}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption">Tax Drag: {dragRate.toFixed(1)}%</Typography>
          <Slider
            value={dragRate}
            onChange={(e, v) => setDragRate(v)}
            min={0} max={15} step={0.5}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption">Life Years: {lifeYears}</Typography>
          <Slider
            value={lifeYears}
            onChange={(e, v) => setLifeYears(v)}
            min={10} max={35} step={1}
            size="small"
            valueLabelDisplay="auto"
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="caption">Dist Method</Typography>
          <ToggleButtonGroup
            value={distMethod}
            exclusive
            onChange={(e, v) => { if (v) setDistMethod(v); }}
            size="small"
          >
            <ToggleButton value="F" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>FA</ToggleButton>
            <ToggleButton value="R" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>RMD</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Terminal values */}
      <Box sx={{ mb: 0.5, textAlign: 'center' }}>
        <Typography variant="body2">
          <strong>Benefit FV:</strong> <span style={{ color: '#2e7d32' }}>${chartData.rmd_fv?.toLocaleString()}</span>&nbsp;&nbsp;<strong>PV:</strong> <span style={{ color: '#2e7d32' }}><strong>${chartData.rmd_pv?.toLocaleString()}</strong></span>
        </Typography>
      </Box>
      </Box>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={chartData.years} margin={{ top: 5, right: 5, left: 5, bottom: -3 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis yAxisId="left" tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v.toFixed(0)}`} />
          <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v.toFixed(0)}`} label={{ value: 'Cumulative Benefit', angle: 90, position: 'insideRight', offset: 3, style: { fontSize: '0.75rem', fill: '#333', fontWeight: 'bold' } }} />
          <RechartsTooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const row = payload[0]?.payload;
            if (!row) return null;
            return (
              <Box sx={{ backgroundColor: 'white', border: '1px solid #ccc', p: 1, fontSize: '0.875rem' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{label} (age {row.age})</Typography>
                {row.tradBal > 0 && <Box sx={{ color: '#ff9800', display: 'flex', gap: 1 }}><span style={{ minWidth: 55, textAlign: 'right' }}>${row.tradBal?.toLocaleString()}</span> After-Tax Principal C(1&minus;tD)</Box>}
                <Box sx={{ color: '#1565c0', display: 'flex', gap: 1 }}><span style={{ minWidth: 55, textAlign: 'right' }}>${row.rothBal?.toLocaleString()}</span> Dist Reinvest - Roth</Box>
                <Box sx={{ color: '#d32f2f', display: 'flex', gap: 1 }}><span style={{ minWidth: 55, textAlign: 'right' }}>${row.taxBal?.toLocaleString()}</span> Dist Reinvest - Tax-Drag</Box>
                <Box sx={{ color: '#2e7d32', fontWeight: 'bold', display: 'flex', gap: 1 }}><span style={{ minWidth: 55, textAlign: 'right' }}>${row.cumulBenefit?.toLocaleString()}</span> Tax-Drag Benefit - Cum</Box>
                {row.rothDist > 0 && (
                  <Box sx={{ color: '#888', mt: 0.5 }}>Distribution: Roth ${Math.round(row.rothDist)?.toLocaleString()} | Taxable ${Math.round(row.taxDist)?.toLocaleString()} | Diff ${Math.round(row.yearlyDiff)?.toLocaleString()}</Box>
                )}
              </Box>
            );
          }} />
          <Legend height={25} />
          <Bar yAxisId="right" dataKey="cumulBenefit" name="Tax-Drag Benefit - Cum" fill="#81c784" stroke="#2e7d32" />
          <Line yAxisId="left" type="monotone" dataKey="tradBal" name="After-Tax Principal C(1−tD)" stroke="#ff9800" strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
          <Line yAxisId="left" type="monotone" dataKey="rothBal" name="Dist Reinvest - Roth" stroke="#1565c0" strokeWidth={1.5} dot={false} />
          <Line yAxisId="left" type="monotone" dataKey="taxBal" name="Dist Reinvest - Tax-Drag" stroke="#d32f2f" strokeWidth={1.5} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TaxDragRmdChart;
